<script lang="ts">
  import type { Api, SearchEvent } from "../lib/api";
  import Card from "../lib/ui/Card.svelte";
  import Pill from "../lib/ui/Pill.svelte";
  import Banner from "../lib/ui/Banner.svelte";
  import EmptyState from "../lib/ui/EmptyState.svelte";
  import { Checkbox } from "@price-check/ui";

  let { api, orgId, orgName }: { api: Api; orgId: string; orgName?: string } = $props();

  let events = $state<SearchEvent[]>([]);
  let error = $state("");
  let live = $state(true);
  let lastUpdated = $state<string>("");

  // Live feed: poll every 5s while `live`. Re-runs if `api`, `orgId`, or `live` change.
  $effect(() => {
    const a = api;
    const scopedOrgId = orgId;
    if (!live) return;
    let alive = true;
    const tick = async () => {
      try {
        const ev = await a.listSearches({ limit: 100, orgId: scopedOrgId });
        if (!alive) return;
        events = ev;
        error = "";
        lastUpdated = new Date().toLocaleTimeString();
      } catch (e) {
        if (alive) error = e instanceof Error ? e.message : "failed to load";
      }
    };
    void tick();
    const intervalId = setInterval(tick, 5000);
    return () => {
      alive = false;
      clearInterval(intervalId);
    };
  });

  // --- Per-supplier breakdown (e.results, stored as CompactResult[] JSON) ------
  // The desktop reports which suppliers were searched + each one's outcome; we render
  // a chip per supplier coloured by status, with the result count when present
  // (matchCount — populated for lookups by newer desktop builds).
  type SupplierResult = {
    id?: string;
    name?: string;
    status?: string;
    matchCount?: number | null;
    price?: number | null;
    currency?: string | null;
    inStock?: boolean | null;
    error?: string | null;
  };

  const suppliersOf = (e: SearchEvent): SupplierResult[] =>
    Array.isArray(e.results) ? (e.results as SupplierResult[]) : [];

  // Status → Pill tone. RFQ rows carry no status, so fall back to "found a price".
  function toneFor(r: SupplierResult): "ok" | "slate" | "amber" | "danger" {
    switch (r.status) {
      case "ok":
        return "ok";
      case "no-session":
      case "blocked":
        return "amber";
      case "error":
      case "config-broken":
        return "danger";
      case "no-match":
      case "not-configured":
        return "slate";
      default:
        return r.price != null ? "ok" : "slate";
    }
  }

  const labelFor = (r: SupplierResult): string => {
    const name = r.name ?? r.id ?? "?";
    return r.matchCount != null ? `${name} · ${r.matchCount}` : name;
  };

  // Hover detail: status · N results · price, plus the failure text when there is one.
  function titleFor(r: SupplierResult): string {
    const bits: string[] = [r.status ?? (r.price != null ? "ok" : "—")];
    if (r.matchCount != null) bits.push(`${r.matchCount} result${r.matchCount === 1 ? "" : "s"}`);
    if (r.price != null) bits.push(`${r.price}${r.currency ? ` ${r.currency}` : ""}`);
    return bits.join(" · ") + (r.error ? `\n${r.error}` : "");
  }

  // The failure texts of a run's non-ok suppliers, for the expandable detail row.
  const failuresOf = (e: SearchEvent): { name: string; error: string }[] =>
    suppliersOf(e)
      .filter((r) => r.error)
      .map((r) => ({ name: r.name ?? r.id ?? "?", error: r.error! }));

  let openId = $state<string | null>(null);

  function ago(iso: string): string {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.floor(s)}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
</script>

<Card pad={false}>
  <header class="flex items-start gap-3 px-4.5 py-4 border-b border-border">
    <div>
      <h3 class="m-0 text-md font-bold">Activity</h3>
      <p class="mt-0.75 mb-0 text-sm text-muted">
        {orgName ? `Searches for ${orgName}, newest first.` : "Searches for this organization, newest first."}
      </p>
    </div>
    <div class="ml-auto flex items-center gap-3.5">
      <label class="flex items-center gap-1.75 text-sm font-semibold text-ink cursor-pointer">
        <Checkbox bind:checked={live} aria-label="Live" />
        <span
          class="w-2 h-2 rounded-full {live
            ? 'bg-success shadow-[0_0_0_3px_var(--color-success-bg)]'
            : 'bg-border-strong'}"
        ></span> Live
      </label>
      {#if lastUpdated}<span class="text-xs text-muted">updated {lastUpdated}</span>{/if}
    </div>
  </header>

  {#if error}<div class="px-4.5 pt-3"><Banner tone="danger">{error}</Banner></div>{/if}

  {#if events.length === 0}
    <EmptyState icon="⌕" title="No searches yet" hint="Lookups will stream in here as users run them." />
  {:else}
    <table class="data-table text-sm! [&_th]:py-2.5 [&_td]:py-2.5">
      <thead>
        <tr>
          <th class="whitespace-nowrap">When</th>
          <th>User</th>
          <th>Part</th>
          <th>Kind</th>
          <th class="text-right! whitespace-nowrap">Hits</th>
          <th>Suppliers</th>
          <th>Source</th>
        </tr>
      </thead>
      <tbody>
        {#each events as e (e.id)}
          <tr>
            <td class="text-muted whitespace-nowrap" title={new Date(e.created_at).toLocaleString()}>{ago(e.created_at)}</td>
            <td>{e.user_email ?? "—"}</td>
            <td class="font-bold font-mono tracking-tight">{e.part}</td>
            <td><Pill tone="indigo">{e.kind}</Pill></td>
            <td class="text-right! whitespace-nowrap">
              <span class="text-success font-bold">{e.ok_count ?? 0}</span><span class="text-muted mx-px">/</span>{e.result_count ?? 0}
            </td>
            <td>
              {#if suppliersOf(e).length}
                <div class="flex flex-wrap gap-1 items-center">
                  {#each suppliersOf(e) as r, i (r.id ?? r.name ?? i)}
                    <span title={titleFor(r)}><Pill tone={toneFor(r)}>{labelFor(r)}</Pill></span>
                  {/each}
                  {#if failuresOf(e).length}
                    <button
                      class="text-xs text-danger font-semibold underline decoration-dotted cursor-pointer bg-transparent border-0 p-0"
                      onclick={() => (openId = openId === e.id ? null : e.id)}
                    >{openId === e.id ? "hide" : "why?"}</button>
                  {/if}
                </div>
              {:else}<span class="text-muted">—</span>{/if}
            </td>
            <td>{#if e.source}<Pill tone="slate">{e.source}</Pill>{:else}<span class="text-muted">—</span>{/if}</td>
          </tr>
          {#if openId === e.id}
            <tr>
              <td colspan="7" class="bg-surface-2!">
                {#each failuresOf(e) as f (f.name)}
                  <div class="flex gap-2 py-0.5 text-xs">
                    <span class="font-semibold shrink-0">{f.name}:</span>
                    <span class="text-muted font-mono break-all">{f.error}</span>
                  </div>
                {/each}
              </td>
            </tr>
          {/if}
        {/each}
      </tbody>
    </table>
  {/if}
</Card>
